import React from 'react';

import styled from 'styled-components';

import Translate from 'src/app_deprecated/components/form/layout/Translate.react';

import { ErrorMessage } from 'src/app/components/lib/error-message';
import { FormControl } from 'src/app/components/lib/form-control';
import { Label } from 'src/app/components/lib/label';
import { Loader } from 'src/app/components/lib/loader';
import { getIDFromLabel } from 'src/app/components/lib/utils';

import { StyledFormTextField } from './form-text-field.styles';

import type { FormControlProps as MUIFormControlProps } from '@mui/material';
import type { TextFieldProps as MUITextFieldProps } from '@mui/material/TextField';

export type TextFieldProps = MUIFormControlProps &
  MUITextFieldProps & {
    errorMessage?: string;
    gridColumnGap?: string;
    gridColumns?: number;
    inputColumnSpan?: string;
    label?: React.ReactElement | string;
    labelColumnSpan?: string;
    labelPlacement?: 'bottom' | 'end' | 'start' | 'top';
    loading?: boolean;
    tooltip?: React.ReactNode;
    useV2Tooltip?: boolean;
    value?: number | string;
    variant?: 'outlined' | 'standard';
    width?: string;
  };

export function FormTextField(props: TextFieldProps): JSX.Element {
  const {
    label,
    id,
    value,
    labelPlacement = 'start',
    width,
    required = false,
    disabled = false,
    sx,
    placeholder,
    rows,
    helperText,
    InputProps,
    className = '',
    tooltip,
    autoFocus = false,
    loading,
    maxRows,
    minRows,
    multiline = false,
    error = false,
    errorMessage,
    autoComplete,
    gridColumnGap,
    gridColumns,
    labelColumnSpan,
    inputColumnSpan,
    useV2Tooltip = false,
    variant = 'standard',
    ...other
  } = props;

  const idFromLabel = getIDFromLabel(label);
  const textFieldId = `text-field-input_${id ?? idFromLabel}`;
  const labelId = `text-field-label_${id ?? idFromLabel}`;

  return (
    <FormControl
      $labelPlacement={label ? labelPlacement : undefined}
      $width={width}
      className={`text-field-form-control ${className ?? ''}`}
      gridColumnGap={gridColumnGap}
      gridColumns={gridColumns}
      hiddenLabel
      multilineLabel={Boolean(multiline && label)}
      sx={sx}
    >
      {label && (
        <Label
          className='text-field-label'
          htmlFor={textFieldId}
          id={labelId}
          required={required}
          style={{ gridColumn: labelColumnSpan }}
          tooltip={tooltip}
          useV2Tooltip={useV2Tooltip}
        >
          <Translate>{label}</Translate>
        </Label>
      )}

      {loading ? (
        <LoaderContainer>
          <Loader variant='black' />
        </LoaderContainer>
      ) : (
        <StyledFormTextField
          {...other}
          $gridColumnInput={inputColumnSpan}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className='text-field-input'
          defaultValue={value}
          disabled={disabled}
          error={!!errorMessage || error}
          fullWidth={!label}
          helperText={helperText}
          id={textFieldId}
          InputProps={InputProps}
          maxRows={maxRows}
          minRows={minRows}
          multiline={multiline}
          placeholder={placeholder}
          required={required}
          rows={rows}
          variant={variant}
        />
      )}

      {errorMessage && (
        <ErrorMessage $fullWidth={!label} error>
          {errorMessage}
        </ErrorMessage>
      )}
    </FormControl>
  );
}

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px;
`;
